import type {
  ImageComponent,
  Placement,
  Rotation,
  SymbolComponent,
} from '@tsagko/label-contracts'
import {
  DEFAULT_HRI_SIZE_PT,
  RENDERABLE_SYMBOLOGIES,
  SYMBOLOGY_METRICS,
  type RenderableSymbology,
} from '@tsagko/label-contracts'

export interface IntrinsicSize {
  wMm: number
  hMm: number
}

export interface RegionBox {
  xMm: number
  yMm: number
  wMm: number
  hMm: number
}

/**
 * Default placement when a component declares none: text at zone top-left.
 * Matches the pre-placement-feature visual behavior so any block that doesn't
 * explicitly set `placement` keeps its existing rendering.
 */
export const DEFAULT_PLACEMENT: Placement = { hAlign: 'left', vAlign: 'top' }

/**
 * Plan-level alignment flag consumed by ^FB (ZPL) and CSS text-align (HTML).
 * 'L' | 'C' | 'R' | 'J' mirrors the legacy TextAlignment range, kept as the
 * plan field because both renderers already consume these one-letter codes.
 */
export type PlanAlignment = 'L' | 'C' | 'R' | 'J'

const PLACEMENT_HALIGN_TO_PLAN: Record<Placement['hAlign'], PlanAlignment> = {
  left: 'L',
  center: 'C',
  right: 'R',
  justify: 'J',
}

/**
 * Translate `placement.hAlign` into the plan's `alignment` field. Defaults to
 * 'L' when no placement is set, matching the pre-placement-feature visual
 * behavior (text flush-left in its wrap box).
 */
export function hAlignToPlan(hAlign: Placement['hAlign'] | undefined): PlanAlignment {
  return hAlign ? PLACEMENT_HALIGN_TO_PLAN[hAlign] : 'L'
}

const H_FACTOR: Record<Placement['hAlign'], number> = {
  left: 0,
  center: 0.5,
  right: 1,
  // ^FB does horizontal justification within the wrap box; the anchor for
  // justified text sits at the left edge of the box.
  justify: 0,
}

const V_FACTOR: Record<Placement['vAlign'], number> = {
  top: 0,
  middle: 0.5,
  bottom: 1,
}

export interface FontVMetrics {
  /** Cap/ascender top as a fraction of the font cell height (0 = cell top). */
  capTopFrac: number
  /** Baseline as a fraction of the font cell height. */
  baselineFrac: number
}

/**
 * `^A0` (Zebra scalable font 0) cap-band metrics, measured via a live-render
 * probe: a known glyph in a cell spanning dots 50→150 rendered through
 * Neodynamic, ink rows read off the 1px=1dot PNG. Digit/cap top sits at the
 * cell top (0.00); baseline at 0.76. See
 * docs/superpowers/specs/2026-05-30-font-aware-vertical-text-placement-design.md
 */
export const A0_VMETRICS: FontVMetrics = { capTopFrac: 0, baselineFrac: 0.76 }

/**
 * Cell-top anchor (mm; top of the FIRST line's font cell) that lands `vm`'s
 * cap band per `vAlign` within `[regionYMm, regionYMm + regionHMm]`:
 *   top    → cap-top at region top
 *   middle → cap-center at region center
 *   bottom → baseline at region bottom
 * For `maxLines > 1` pass `lines`/`lineSpacingMm`; the cap band then spans the
 * first line's cap-top to the last line's baseline. Collapses to today's
 * cell-centering when `capTopFrac = 0` and `baselineFrac = 1`.
 */
export function capBandAnchorY(
  regionYMm: number,
  regionHMm: number,
  fontHeightMm: number,
  vAlign: Placement['vAlign'],
  vm: FontVMetrics,
  opts: { lines?: number; lineSpacingMm?: number } = {},
): number {
  const lines = Math.max(1, opts.lines ?? 1)
  const lineSpacingMm = opts.lineSpacingMm ?? 0
  const capTopOffsetMm = vm.capTopFrac * fontHeightMm
  const capBandHMm =
    (lines - 1) * (fontHeightMm + lineSpacingMm) +
    (vm.baselineFrac - vm.capTopFrac) * fontHeightMm
  const capTopTargetY = regionYMm + (regionHMm - capBandHMm) * V_FACTOR[vAlign]
  return capTopTargetY - capTopOffsetMm
}

/**
 * Plan-level text orientation — the literal `^A0` orientation character.
 * Probe-verified against the live Neodynamic renderer (2026-06-11): these
 * four are the only valid values; anything else (e.g. the `L` the pipeline
 * shipped before issue #1) silently falls back to unrotated rendering.
 */
export type PlanRotation = 'N' | 'R' | 'I' | 'B'

const ROTATION_MAP: Record<Rotation, PlanRotation> = {
  0: 'N',
  90: 'R', // 90° clockwise
  180: 'I', // inverted
  270: 'B', // bottom-up
}

/** Authoring `rotation` (degrees) → `^A0` orientation char. */
export function rotationToPlan(r: Rotation | undefined): PlanRotation {
  return r ? ROTATION_MAP[r] : 'N'
}

/** Flow-axis ^FB letter mirror for orientations that read against the page
 *  axis (I reads right-to-left, B reads bottom-up). J is symmetric. */
const H_LETTER_MIRROR: Record<PlanAlignment, PlanAlignment> = {
  L: 'R',
  C: 'C',
  R: 'L',
  J: 'J',
}

/** vAlign → flow-axis ^FB letter for R (flows down: top = line start). */
const V_TO_LETTER: Record<Placement['vAlign'], PlanAlignment> = {
  top: 'L',
  middle: 'C',
  bottom: 'R',
}

/** vAlign → flow-axis ^FB letter for B (flows up: bottom = line start). */
const V_TO_LETTER_MIRROR: Record<Placement['vAlign'], PlanAlignment> = {
  top: 'R',
  middle: 'C',
  bottom: 'L',
}

/** Cross-axis cap-band pin for I: the cell's local-top faces page-BOTTOM, so
 *  page-top placement pins the baseline ('bottom' reference) and vice versa. */
const V_PIN_FLIP: Record<Placement['vAlign'], Placement['vAlign']> = {
  top: 'bottom',
  middle: 'middle',
  bottom: 'top',
}

/** Cross-axis pin for R: local-top faces page-RIGHT → page-left pins the
 *  baseline. Justify behaves like left on the cross axis (H_FACTOR = 0). */
const H_PIN_R: Record<Placement['hAlign'], Placement['vAlign']> = {
  left: 'bottom',
  center: 'middle',
  right: 'top',
  justify: 'bottom',
}

/** Cross-axis pin for B: local-top faces page-LEFT → page-left pins cap-top. */
const H_PIN_B: Record<Placement['hAlign'], Placement['vAlign']> = {
  left: 'top',
  center: 'middle',
  right: 'bottom',
  justify: 'top',
}

/**
 * The text-plan fields that depend on placement + rotation. Both planners
 * (`render-plan.ts` and `build-authoring-plan.ts`, held equal by parity
 * tests) consume this single helper so the rotated-anchor math exists once.
 */
export interface TextPlanGeometry {
  anchorXMm: number
  anchorYMm: number
  alignment: PlanAlignment
  wrapBoxWidthMm: number
  /** Cap-band pin for the canvas (see `RenderPlan['vAlign']`). */
  vAlign: Placement['vAlign']
}

/**
 * Resolve a text component's placement + rotation into its `^FO` anchor,
 * `^FB` block width/letter, and the canvas cap-band pin.
 *
 * Geometry model (probe-verified on the live Neodynamic renderer,
 * 2026-06-11 — see test/rotated-text.test.ts for the full probe notes):
 * WITH a `^FB` block, `^FO` is the TOP-LEFT of the rotated text block's page
 * bounding box for all four orientations, and `^FB`'s width acts along the
 * text's local flow axis (page-vertical for R/B). So:
 *
 *   - the wrap box length is the region extent along the page axis the text
 *     flows on (width for N/I, height for R/B);
 *   - the flow-axis alignment letter comes from the placement control that
 *     owns that page axis, mirrored when the text reads against it
 *     (I right-to-left, B bottom-up);
 *   - the cross-axis anchor pins the CAP BAND (`capBandAnchorY` semantics)
 *     along the page axis the font cell maps to, with the band offset from
 *     `^FO` determined by which way the cell's local-top edge faces:
 *     capTop for N/B (top faces page-top/page-left), (h - baseline) for R/I
 *     (top faces page-right/page-bottom).
 *
 * Rotated anchors clamp at 0: the renderer parses negative `^FO` coords as
 * their absolute value (probe: y=-24 rendered at y=+24). The N arm is
 * byte-identical to the pre-rotation code path (no clamp — it cannot go
 * negative for in-label regions).
 */
export function textPlanGeometry(args: {
  region: RegionBox
  fontHeightMm: number
  placement: Placement
  rotation: PlanRotation
  lines?: number
  lineSpacingMm?: number
  vm?: FontVMetrics
}): TextPlanGeometry {
  const { region, fontHeightMm, placement, rotation } = args
  const vm = args.vm ?? A0_VMETRICS
  const lines = Math.max(1, args.lines ?? 1)
  const lineSpacingMm = args.lineSpacingMm ?? 0
  if (rotation === 'N') {
    return {
      anchorXMm: region.xMm,
      anchorYMm: capBandAnchorY(
        region.yMm, region.hMm, fontHeightMm, placement.vAlign, vm,
        { lines, lineSpacingMm },
      ),
      alignment: hAlignToPlan(placement.hAlign),
      wrapBoxWidthMm: region.wMm,
      vAlign: placement.vAlign,
    }
  }
  const capTopMm = vm.capTopFrac * fontHeightMm
  const baselineMm = vm.baselineFrac * fontHeightMm
  // First line's cap-top → last line's baseline (same formula as
  // capBandAnchorY). Line-stacking starts at the ^FO edge for every
  // orientation (probe: multi-line blocks stack N→down, R→right, I→down,
  // B→right), so the band start offset from ^FO is line-count-independent.
  const bandHMm =
    (lines - 1) * (fontHeightMm + lineSpacingMm) + (baselineMm - capTopMm)
  switch (rotation) {
    case 'I': {
      const bandStart =
        region.yMm + (region.hMm - bandHMm) * V_FACTOR[placement.vAlign]
      return {
        anchorXMm: region.xMm,
        anchorYMm: Math.max(0, bandStart - (fontHeightMm - baselineMm)),
        alignment: H_LETTER_MIRROR[hAlignToPlan(placement.hAlign)],
        wrapBoxWidthMm: region.wMm,
        vAlign: V_PIN_FLIP[placement.vAlign],
      }
    }
    case 'R': {
      const bandStart =
        region.xMm + (region.wMm - bandHMm) * H_FACTOR[placement.hAlign]
      return {
        anchorXMm: Math.max(0, bandStart - (fontHeightMm - baselineMm)),
        anchorYMm: region.yMm,
        alignment: V_TO_LETTER[placement.vAlign],
        wrapBoxWidthMm: region.hMm,
        vAlign: H_PIN_R[placement.hAlign],
      }
    }
    case 'B': {
      const bandStart =
        region.xMm + (region.wMm - bandHMm) * H_FACTOR[placement.hAlign]
      return {
        anchorXMm: Math.max(0, bandStart - capTopMm),
        anchorYMm: region.yMm,
        alignment: V_TO_LETTER_MIRROR[placement.vAlign],
        wrapBoxWidthMm: region.hMm,
        vAlign: H_PIN_B[placement.hAlign],
      }
    }
  }
}

/**
 * Resolve placement + region box + intrinsic content size into a final anchor
 * (top-left of the content's bounding box in mm). Clamped so the anchor never
 * escapes the region: when intrinsic exceeds region on either axis, the anchor
 * for that axis pins to the region's top/left edge.
 */
export function resolveAnchor(
  region: RegionBox,
  intrinsic: IntrinsicSize,
  placement: Placement = DEFAULT_PLACEMENT,
): { anchorXMm: number; anchorYMm: number } {
  const slackW = Math.max(0, region.wMm - intrinsic.wMm)
  const slackH = Math.max(0, region.hMm - intrinsic.hMm)
  return {
    anchorXMm: region.xMm + slackW * H_FACTOR[placement.hAlign],
    anchorYMm: region.yMm + slackH * V_FACTOR[placement.vAlign],
  }
}

/**
 * Text bounding box: wrap box for width (horizontal placement is handled by
 * `^FB` inside that box), single cell height × maxLines + inter-line spacing.
 * Coerces `maxLines < 1` to 1 so single-line text always gets at least one cell.
 */
export function textIntrinsicSize(args: {
  fontHeightMm: number
  maxLines: number
  lineSpacingMm: number
  wrapBoxWidthMm: number
}): IntrinsicSize {
  const lines = Math.max(1, args.maxLines)
  return {
    wMm: args.wrapBoxWidthMm,
    hMm: args.fontHeightMm * lines + args.lineSpacingMm * (lines - 1),
  }
}

/**
 * Image intrinsic size — currently always returns `{ wMm: 0, hMm: 0 }`.
 *
 * `ImageComponentSchema` does not declare `widthMm`/`heightMm` today, so every
 * image's intrinsic footprint is zero. Callers that get a zero-sized intrinsic
 * must fall back to filling the zone (the image planner's pre-placement
 * behavior) — `resolveAnchor` would otherwise push the anchor by the full
 * region slack, which is meaningless for a zero-sized rect.
 *
 * Net effect for this slice: image placement is a no-op (images keep filling
 * their zone). When a future slice adds explicit dimensions to the image
 * schema, this helper returns those values and image placement becomes
 * meaningful with no plan-builder change required.
 */
export function imageIntrinsicSize(_c: ImageComponent): IntrinsicSize {
  return { wMm: 0, hMm: 0 }
}

const RENDERABLE = new Set<string>(RENDERABLE_SYMBOLOGIES)

const PT_TO_MM = 25.4 / 72

/**
 * Vertical gap between the bottom of the bars and the top of the
 * pipeline-emitted human-readable line. Probe-measured off the NATIVE ^BC
 * interpretation line (Neodynamic, 203dpi, 2026-06-11): HRI ink starts 6
 * dots (≈0.75mm) below the bars regardless of `^BY`.
 */
export const HRI_GAP_MM = 0.75

/**
 * Full intrinsic footprint of a symbol — the SYMBOL BOX. Per GS1/ISO the
 * quiet zones are part of the symbol, so:
 *
 *   wMm = quietLeft + bars + quietRight   (modules × X-dimension)
 *   hMm = quietTop + bars + quietBottom + hriBand
 *
 * `resolveAnchor` positions this box in the region; the ZPL emitter offsets
 * `^FO` by (quietLeftMm, quietTopMm) so the first bar sits exactly one quiet
 * zone in from the box edge. Linear codes have no vertical quiet zone; QR has
 * 4 modules on all four sides.
 *
 * `hriBandMm` (gap + HRI font cell) is non-zero only for code-128 / gs1-128
 * with humanReadable on — those get a pipeline-emitted ^A0 line below the
 * bars (the native ^BC line is disabled; see render-plan.ts). EAN-13 keeps
 * the NATIVE ^BE interpretation line whose below-bars height is not modeled
 * (pre-existing behavior); QR has no HRI.
 *
 * Non-renderable symbologies return wMm 0, which signals the caller to fall
 * back to zone width.
 *
 * When `dpi` is provided the module width is snapped to the nearest whole-dot
 * value (clamped 1–10, matching what `^BY` sends to the printer) so the
 * canvas/anchor width is exact WYSIWYG with the printed output. Without `dpi`
 * (pure unit tests) the raw mm value is used unchanged.
 */
export interface BarcodeSymbolBox extends IntrinsicSize {
  quietLeftMm: number
  quietRightMm: number
  quietTopMm: number
  quietBottomMm: number
  /** Bar-band height (linear) / inner symbol side (QR) in mm. */
  barsHeightMm: number
  /** HRI_GAP_MM + HRI font cell height when the pipeline emits its own HRI line, else 0. */
  hriBandMm: number
}

export function barcodeIntrinsicSize(c: SymbolComponent, payload: string, dpi?: number): BarcodeSymbolBox {
  const sym = c.symbology
  if (!RENDERABLE.has(sym)) {
    const hMm = c.heightMm ?? 0
    return {
      wMm: 0, hMm,
      quietLeftMm: 0, quietRightMm: 0, quietTopMm: 0, quietBottomMm: 0,
      barsHeightMm: hMm, hriBandMm: 0,
    }
  }
  const metrics = SYMBOLOGY_METRICS[sym as RenderableSymbology]
  const rawModuleWidthMm = c.moduleWidthMm ?? metrics.defaultModuleWidthMm
  // Match what the printer actually renders: ^BY uses whole dots, clamped 1-10.
  // When dpi is known, snap the canvas/anchor width to that same dot grid so
  // the preview is exact WYSIWYG. Without dpi (pure unit tests), use raw mm.
  const moduleWidthMm =
    dpi !== undefined
      ? (Math.max(1, Math.min(10, Math.round((rawModuleWidthMm * dpi) / 25.4))) * 25.4) / dpi
      : rawModuleWidthMm
  const qz = metrics.quietZoneModules
  const quietLeftMm = qz.left * moduleWidthMm
  const quietRightMm = qz.right * moduleWidthMm
  const quietTopMm = qz.top * moduleWidthMm
  const quietBottomMm = qz.bottom * moduleWidthMm
  switch (sym) {
    case 'code-128':
    case 'gs1-128': {
      const modules = code128ModuleCount(payload, sym === 'gs1-128')
      const barsHeightMm = c.heightMm ?? metrics.defaultHeightMm
      const hriOn = c.humanReadable ?? metrics.defaultHumanReadable
      const hriBandMm = hriOn
        ? HRI_GAP_MM + (c.humanReadableSizePt ?? DEFAULT_HRI_SIZE_PT) * PT_TO_MM
        : 0
      return {
        wMm: (qz.left + modules + qz.right) * moduleWidthMm,
        hMm: barsHeightMm + hriBandMm,
        quietLeftMm, quietRightMm, quietTopMm, quietBottomMm,
        barsHeightMm, hriBandMm,
      }
    }
    case 'ean-13': {
      const barsHeightMm = c.heightMm ?? metrics.defaultHeightMm
      return {
        wMm: (qz.left + metrics.fixedModuleCount! + qz.right) * moduleWidthMm,
        hMm: barsHeightMm, // native ^BE HRI height below bars not modeled (pre-existing)
        quietLeftMm, quietRightMm, quietTopMm, quietBottomMm,
        barsHeightMm, hriBandMm: 0,
      }
    }
    case 'itf': {
      // Module count is data-INDEPENDENT (see registry.ts's itf entry for
      // the start/pair/stop derivation) — same fixed-count shape as ean-13.
      const barsHeightMm = c.heightMm ?? metrics.defaultHeightMm
      return {
        wMm: (qz.left + metrics.fixedModuleCount! + qz.right) * moduleWidthMm,
        hMm: barsHeightMm, // native ^B2 HRI height below bars not modeled (mirrors ean-13)
        quietLeftMm, quietRightMm, quietTopMm, quietBottomMm,
        barsHeightMm, hriBandMm: 0,
      }
    }
    case 'qr': {
      const side = qrModuleCountFor(payload.length) * moduleWidthMm
      return {
        wMm: side + quietLeftMm + quietRightMm,
        hMm: side + quietTopMm + quietBottomMm, // heightMm intentionally ignored (square)
        quietLeftMm, quietRightMm, quietTopMm, quietBottomMm,
        barsHeightMm: side, hriBandMm: 0,
      }
    }
    default: {
      const hMm = c.heightMm ?? 0
      return {
        wMm: 0, hMm,
        quietLeftMm: 0, quietRightMm: 0, quietTopMm: 0, quietBottomMm: 0,
        barsHeightMm: hMm, hriBandMm: 0,
      }
    }
  }
}

const SYMBOL_MODULES = 11
const STOP_MODULES = 13 // stop pattern incl. the 2-module termination bar

/**
 * Code-128 / GS1-128 module count, EXCLUDING quiet zones (matches the bar
 * extent the printer draws from `^FO`). Ground truth established 2026-06-11
 * by rendering ^BC payloads through the live Neodynamic backend at ^BY2 and
 * pixel-decoding the 1px=1dot PNGs back into Code-128 symbol sequences:
 *
 *   `gs1 = false` — our code-128 form `^BCN,h,f,g,N` (mode N, no mode
 *   param): the renderer does NO numeric compaction. Every payload decoded
 *   as StartB + one subset-B symbol per character + check digit. So
 *   modules = 11×(len + 2) + 13. (A 17-digit payload is 222 modules, not
 *   the 134 the old subset-C pairing model predicted — issue #4.)
 *
 *   `gs1 = true` — our gs1-128 form `^BC…,N,N,A` (mode A) with the `>8`
 *   FNC1 payload convention: mode A auto-inserts a leading FNC1 symbol IN
 *   ADDITION to our explicit `>8` (every probe decoded Start + FNC1 + FNC1
 *   + …), then encodes minimally: StartC / subset-C digit pairs where they
 *   pay off, CodeB/CodeC switches otherwise, FNC1 separators encodable in
 *   either subset. A minimal-symbol-count DP over subsets {B, C} (below)
 *   reproduced all 15 probed symbol sequences exactly.
 *
 * Limitations: subset A (control chars) is not modelled — uppercase+digits
 * payloads encode identically in A and B, and ^FD can't carry control
 * chars. Non-`>8` invocation codes (`>;` etc.) are counted as ordinary
 * characters, same as the previous model.
 */
export function code128ModuleCount(payload: string, gs1: boolean): number {
  if (!gs1) {
    // Mode N: StartB + one symbol per char + check, then the stop pattern.
    return SYMBOL_MODULES * (payload.length + 2) + STOP_MODULES
  }

  // Mode A. Tokenize: each `>8` is one FNC1 symbol; mode A prepends its own.
  type Token = { fnc1: true } | { fnc1: false; digit: boolean }
  const tokens: Token[] = [{ fnc1: true }] // mode A's automatic leading FNC1
  for (let i = 0; i < payload.length; i++) {
    if (payload[i] === '>' && payload[i + 1] === '8') {
      tokens.push({ fnc1: true })
      i++
    } else {
      const ch = payload[i]!
      tokens.push({ fnc1: false, digit: ch >= '0' && ch <= '9' })
    }
  }

  // Minimal-symbol DP. dpB[i] / dpC[i] = fewest symbols (including the
  // start code) that encode tokens[0..i) and leave the encoder in subset
  // B / C. Transitions cost 1 symbol per token (+1 for a CodeB/CodeC
  // switch); a digit PAIR consumes two tokens for 1 symbol in subset C.
  const n = tokens.length
  const INF = Number.POSITIVE_INFINITY
  const dpB = new Array<number>(n + 1).fill(INF)
  const dpC = new Array<number>(n + 1).fill(INF)
  dpB[0] = 1 // StartB
  dpC[0] = 1 // StartC
  for (let i = 0; i < n; i++) {
    const t = tokens[i]!
    // Any single token (char or FNC1) encodes as one subset-B symbol.
    const viaB = Math.min(dpB[i]!, dpC[i]! + 1) + 1
    if (viaB < dpB[i + 1]!) dpB[i + 1] = viaB
    if (t.fnc1) {
      // FNC1 also exists in subset C — stays in C across AI separators.
      const viaC = Math.min(dpC[i]!, dpB[i]! + 1) + 1
      if (viaC < dpC[i + 1]!) dpC[i + 1] = viaC
    } else if (t.digit) {
      const next = tokens[i + 1]
      if (next && !next.fnc1 && next.digit) {
        // A digit pair packs into one subset-C symbol.
        const viaC = Math.min(dpC[i]!, dpB[i]! + 1) + 1
        if (viaC < dpC[i + 2]!) dpC[i + 2] = viaC
      }
    }
  }
  const dataAndStart = Math.min(dpB[n]!, dpC[n]!)
  return SYMBOL_MODULES * (dataAndStart + 1) + STOP_MODULES // +1 = check digit
}

// Coarse module-count selector. Real QR sizing depends on error-correction
// level and payload alphabet; this returns the smallest version whose data
// capacity at error-correction level M covers payload.length bytes. ±1 module
// at common DPIs is sub-mm; good enough for placement positioning.
function qrModuleCountFor(payloadLen: number): number {
  if (payloadLen <= 14) return 21   // v1
  if (payloadLen <= 26) return 25   // v2
  if (payloadLen <= 62) return 33   // v4
  if (payloadLen <= 122) return 45  // v7
  if (payloadLen <= 500) return 77  // ~v17
  return 117                        // ~v33 (largest practical)
}
